import React, { useCallback, useState } from "react";
import {
  FlatList,
  StyleSheet,
  RefreshControl,
  TouchableOpacity,
  Linking,
} from "react-native";
import { graphql } from "react-relay";
import { useMutation, usePaginationFragment } from "react-relay/hooks";
import { View, Card, Text } from "@components/atoms/Themed";
import Loading from "@components/atoms/Loading";
import NotificationListItem from "@components/molecules/NotificationListItem";
import useMessage from "@hooks/useMessage";
import { PagingNotification } from "@constants/App";
import Colors from "@constants/Colors";
import Fonts from "@constants/Fonts";
import { schema } from "@navigation/LinkingConfiguration";
import { getPathFromUniversalLinks } from "@navigation/navigate";
import NotificationInfoModal from "@components/organisms/NotificationInfoModal";
import type { NotificationInfoModalData } from "@components/organisms/NotificationInfoModal";
import { NotificationsPagination$key } from "@generated/NotificationsPagination.graphql";
import { NotificationsReadMutation } from "@generated/NotificationsReadMutation.graphql";
import { NotificationsReadAllMutation } from "@generated/NotificationsReadAllMutation.graphql";

const paginationQuery = graphql`
  fragment NotificationsPagination on Query
  @refetchable(queryName: "NotificationsPaginationQuery")
  @argumentDefinitions(
    after: { type: "String" }
    first: { type: "Int" }
  ) {
    notifications(first: $first, after: $after)
      @connection(key: "NotificationsList_notifications") {
      edges {
        node {
          id
          seen
          url
          infoModal {
            __typename
            ... on CancelCampaignInfoModal {
              campaignName
              subtitle
              canceledAt
              reason
              reasonLabel
            }
            ... on CandidatePriceChangedInfoModal {
              campaignName
              subtitle
              changedAt
              oldPrice
              newPrice
              priceText
              detailButtonLabel
            }
            ... on ReRecruitCampaignInfoModal {
              campaignName
              reRecruitAt
              content
              reason
              reasonLabel
            }
          }
          pushCampaignMessage {
            __typename
            title
            body
          }
          ...NotificationListItem_notification
        }
      }
    }
  }
`;

const readMutation = graphql`
  mutation NotificationsReadMutation($input: ReadNotificationMutationInput!) {
    readNotification(input: $input) {
      __typename
      ... on ReadNotificationSuccess {
        notification {
          id
          seen
        }
        notificationUnreadTotal {
          id
          count
        }
      }
      ... on ReadNotificationError {
        message
      }
    }
  }
`;

const readAllMutation = graphql`
  mutation NotificationsReadAllMutation(
    $input: ReadAllNotificationsMutationInput!
  ) {
    readAllNotifications(input: $input) {
      __typename
      ... on ReadAllNotificationsSuccess {
        notificationUnreadTotal {
          id
          count
        }
      }
      ... on ReadAllNotificationsError {
        message
      }
    }
  }
`;

type Props = {
  fragmentRef: NotificationsPagination$key;
};

export default function Notifications({ fragmentRef }: Props): JSX.Element {
  const { data, loadNext, hasNext, refetch } = usePaginationFragment(
    paginationQuery,
    fragmentRef
  );
  const [commitRead] = useMutation<NotificationsReadMutation>(readMutation);
  const [commitReadAll] =
    useMutation<NotificationsReadAllMutation>(readAllMutation);
  const { set: setMessage } = useMessage();

  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [paging, setPaging] = useState<boolean>(false);
  const [busy, setBusy] = useState<boolean>(false);
  // 中止・金額変更・再募集・運営からの配信は、タップ時に詳細モーダルを開く。
  // その他の通知はリンク先へ遷移する。
  const [activeModal, setActiveModal] = useState<{
    data: NotificationInfoModalData;
    url: string | null;
  } | null>(null);

  const navigateToUrl = useCallback((url: string): void => {
    // push 通知タップと同じ deep link 経路に揃える。
    // schema (= Linking.createURL("/")) は末尾 `/` を含むため、先頭 `/` を剥がして二重 slash を防ぐ。
    let path = getPathFromUniversalLinks(url);
    // local 開発時 backend が `http://localhost:19006/...` のような現在のアプリ origin と一致する
    // full URL を返すケースを deep link 化する。stg/prd では UniversalLinkPrefix で先に拾われる。
    if (path === null && url.startsWith(schema)) {
      path = url.slice(schema.length);
    }
    if (path !== null) {
      Linking.openURL(`${schema}${path.replace(/^\/+/, "")}`).catch(() => {});
    } else if (!/^https?:\/\//.test(url)) {
      Linking.openURL(`${schema}${url.replace(/^\/+/, "")}`).catch(() => {});
    }
  }, []);

  const onItemPress = useCallback(
    async ({
      id,
      seen,
      url,
      infoModal,
    }: {
      id: string;
      seen: boolean;
      url: string | null;
      infoModal: NotificationInfoModalData | null;
    }): Promise<void> => {
      if (busy) return;
      if (!seen) {
        setBusy(true);
        await new Promise<void>((resolve) => {
          commitRead({
            variables: { input: { id } },
            onCompleted: () => resolve(),
            onError: () => resolve(),
          });
        });
        setBusy(false);
      }
      if (infoModal != null) {
        setActiveModal({ data: infoModal, url });
        return;
      }
      if (url == null || url === "") return;
      navigateToUrl(url);
    },
    [busy, commitRead, navigateToUrl]
  );

  const onReadAll = useCallback(() => {
    if (busy) return;
    setBusy(true);
    commitReadAll({
      variables: { input: {} },
      onCompleted: (res) => {
        if (
          res.readAllNotifications.__typename === "ReadAllNotificationsSuccess"
        ) {
          refetch(
            { first: PagingNotification },
            {
              fetchPolicy: "network-only",
              onComplete: () => setBusy(false),
            }
          );
        } else {
          const message =
            res.readAllNotifications.__typename === "ReadAllNotificationsError"
              ? res.readAllNotifications.message
              : "既読にできませんでした";
          setMessage({ title: message, mode: "error" });
          setBusy(false);
        }
      },
      onError: () => {
        setMessage({ title: "既読にできませんでした", mode: "error" });
        setBusy(false);
      },
    });
  }, [busy, commitReadAll, refetch, setMessage]);

  const edges = data.notifications?.edges ?? [];

  return (
    <View style={styles.container}>
      <Card style={styles.bar}>
        <TouchableOpacity onPress={onReadAll}>
          <Text style={styles.readAll}>すべて既読にする</Text>
        </TouchableOpacity>
      </Card>

      <FlatList
        data={edges}
        keyExtractor={(item) => item.node.id}
        ListEmptyComponent={
          <View style={styles.empty}>
            <Text style={styles.emptyText}>お知らせはありません</Text>
          </View>
        }
        onEndReached={() => {
          if (hasNext && !paging) {
            setPaging(true);
            loadNext(PagingNotification, {
              onComplete: () => setPaging(false),
            });
          }
        }}
        refreshControl={
          <RefreshControl
            onRefresh={() => {
              if (refreshing) return;
              setRefreshing(true);
              refetch(
                { first: PagingNotification },
                {
                  fetchPolicy: "network-only",
                  onComplete: () => setRefreshing(false),
                }
              );
            }}
            refreshing={refreshing}
          />
        }
        renderItem={({ item }) => {
          const { infoModal: rawInfoModal, pushCampaignMessage } = item.node;
          let infoModal: NotificationInfoModalData | null = null;
          if (pushCampaignMessage != null) {
            infoModal = pushCampaignMessage;
          } else if (
            rawInfoModal != null &&
            rawInfoModal.__typename !== "%other"
          ) {
            infoModal = rawInfoModal;
          }
          return (
            <NotificationListItem
              fragmentRef={item.node}
              onPress={({ id, seen, url }) =>
                onItemPress({ id, seen, url, infoModal })
              }
            />
          );
        }}
      />

      {(paging || busy) && (
        <View style={styles.paging}>
          <Loading />
        </View>
      )}

      {activeModal !== null && (
        <NotificationInfoModal
          data={activeModal.data}
          onClose={() => setActiveModal(null)}
          onPressCampaignDetail={
            activeModal.url != null && activeModal.url !== ""
              ? () => {
                  const { url } = activeModal;
                  setActiveModal(null);
                  if (url != null && url !== "") navigateToUrl(url);
                }
              : undefined
          }
        />
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  bar: {
    flexDirection: "row",
    justifyContent: "flex-end",
    paddingHorizontal: 16,
    paddingVertical: 8,
  },
  readAll: {
    ...Fonts.sb100,
    color: Colors.green,
  },
  empty: {
    paddingVertical: 64,
    alignItems: "center",
    justifyContent: "center",
  },
  emptyText: {
    ...Fonts.mr100,
    color: Colors.gray,
  },
  paging: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    paddingVertical: 12,
  },
});
